import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import type { Database } from '@/lib/supabase'

type AccountContact = Database['public']['Tables']['account_contacts']['Row']

interface UseContactsReturn {
  contacts: AccountContact[]
  setContacts: React.Dispatch<React.SetStateAction<AccountContact[]>>
  loading: boolean
  error: string | null
  setError: React.Dispatch<React.SetStateAction<string | null>>
  createContact: (data: Omit<AccountContact, 'id' | 'account_id' | 'created_at' | 'updated_at'>) => Promise<boolean>
  updateContact: (id: string, data: Partial<AccountContact>) => Promise<boolean>
  deleteContact: (id: string) => Promise<boolean>
  refetch: () => Promise<void>
}

export const useContacts = (): UseContactsReturn => {
  const { user } = useAuth()
  const [contacts, setContacts] = useState<AccountContact[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchContacts = async (): Promise<void> => {
    if (!user?.account_id || !supabase) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      const { data: contactsData, error: contactsError } = await supabase
        .from('account_contacts')
        .select('*')
        .eq('account_id', user.account_id)
        .order('created_at', { ascending: false })

      if (contactsError) throw contactsError

      setContacts(contactsData || [])
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar contatos')
    } finally {
      setLoading(false)
    }
  }

  const refetch = async (): Promise<void> => {
    await fetchContacts()
  }

  const createContact = async (data: Omit<AccountContact, 'id' | 'account_id' | 'created_at' | 'updated_at'>): Promise<boolean> => {
    if (!user?.account_id || !supabase) return false

    try {
      setError(null)
      const { error } = await supabase
        .from('account_contacts')
        .insert({ ...data, account_id: user.account_id })

      if (error) throw error

      await fetchContacts()
      return true
    } catch (err: any) {
      setError(err.message || 'Erro ao criar contato')
      return false
    }
  }

  const updateContact = async (id: string, data: Partial<AccountContact>): Promise<boolean> => {
    if (!user?.account_id || !supabase) return false

    try {
      setError(null)
      const { error } = await supabase
        .from('account_contacts')
        .update(data)
        .eq('id', id)
        .eq('account_id', user.account_id)

      if (error) throw error

      await fetchContacts()
      return true
    } catch (err: any) {
      setError(err.message || 'Erro ao atualizar contato')
      return false
    }
  }

  const deleteContact = async (id: string): Promise<boolean> => {
    if (!user?.account_id || !supabase) return false

    try {
      setError(null)
      const { error } = await supabase
        .from('account_contacts')
        .delete()
        .eq('id', id)
        .eq('account_id', user.account_id)

      if (error) throw error

      await fetchContacts()
      return true
    } catch (err: any) {
      setError(err.message || 'Erro ao deletar contato')
      return false
    }
  }

  useEffect(() => {
    if (!user?.account_id || !supabase) {
      return
    }

    console.log('🔄 [useContacts] Setting up contacts subscription')

    fetchContacts()

    const channel = supabase
      .channel('contacts-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'account_contacts',
          filter: `account_id=eq.${user.account_id}`
        },
        (payload) => {
          console.log('📇 [useContacts] Contact change detected:', payload.eventType)

          if (payload.eventType === 'INSERT') {
            const newContact = payload.new as AccountContact
            setContacts(prev => {
              const exists = prev.some(c => c.id === newContact.id)
              if (exists) return prev
              console.log('✅ [useContacts] Adding new contact')
              return [newContact, ...prev]
            })
          } else if (payload.eventType === 'UPDATE') {
            const updatedContact = payload.new as AccountContact
            setContacts(prev => {
              console.log('🔄 [useContacts] Updating contact:', updatedContact.id)
              return prev.map(c => c.id === updatedContact.id ? updatedContact : c)
                .sort((a, b) => {
                  const aTime = a.created_at ? new Date(a.created_at).getTime() : 0
                  const bTime = b.created_at ? new Date(b.created_at).getTime() : 0
                  return bTime - aTime
                })
            })
          } else if (payload.eventType === 'DELETE') {
            const deletedId = (payload.old as AccountContact).id
            setContacts(prev => {
              console.log('🗑️ [useContacts] Removing contact:', deletedId)
              return prev.filter(c => c.id !== deletedId)
            })
          }
        }
      )
      .subscribe((status) => {
        console.log('🔌 [useContacts] Subscription status:', status)
      })

    return () => {
      console.log('🔌 [useContacts] Unsubscribing from contacts')
      if (supabase) {
        supabase.removeChannel(channel)
      }
    }
  }, [user?.account_id])

  return {
    contacts,
    setContacts,
    loading,
    error,
    setError,
    createContact,
    updateContact,
    deleteContact,
    refetch,
  }
}
